import {
  createFileRoute,
  redirect,
  isRedirect,
} from '@tanstack/react-router'
import {
  hasAccessToken,
  getAccessToken,
  saveAccessToken,
  clearTokens,
} from '@/lib/token-storage'
import { validateOtpByToken } from '@/api/auth/auth.actions'
import { getUserProfile } from '@/api/workspace/profile.actions'
import { userRequiresPhoneVerification } from '@/lib/phone-verification-gate'
import {
  getMainAppHandoffHref,
  getSuporteAppBaseUrl,
  getSuporteAppHandoffHref,
  getAdmindeskAppBaseUrl,
  getAdmindeskAppHandoffHref,
} from '@/lib/post-login-redirect'
import type { ApiErrorResponse } from '@/api/auth/auth.types'
import { syncImobdeskEntryAppFromSearch } from '@/lib/imobdesk-entry-app'
import { revokeRefreshCookieSession } from '@/lib/handle-signed-out-query'
import { isSignedOutSearch } from '@/lib/signed-out-query'
import {
  buildLoginLinkHrefWithoutSignedOut,
  loginLinkSearchSchema,
  normalizeLoginLinkToken,
  preservedLoginSearch,
} from '@/lib/login-link-token'
import { API_BASE_URL } from '@/config/api'

async function bootstrapRefreshCookie(accessToken: string): Promise<void> {
  const base = API_BASE_URL ? API_BASE_URL.replace(/\/$/, '') : ''
  try {
    await fetch(`${base}/api/v2/auth/bootstrap-refresh-cookie`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
  } catch {
    // segue com access token em memória
  }
}

export const Route = createFileRoute('/l/$token')({
  validateSearch: loginLinkSearchSchema,
  beforeLoad: async ({ params, search }) => {
    syncImobdeskEntryAppFromSearch(search)
    const signedOut = isSignedOutSearch(
      search,
      typeof window !== 'undefined' ? window.location.search : undefined,
    )
    if (signedOut) {
      await revokeRefreshCookieSession()
      clearTokens()
      throw redirect({
        href: buildLoginLinkHrefWithoutSignedOut(params.token, search),
        replace: true,
      })
    }
    const mainBase = (
      import.meta.env.VITE_MAIN_APP_URL as string | undefined
    )?.replace(/\/$/, '')

    if (hasAccessToken()) {
      const at = getAccessToken()
      if (at) {
        try {
          const profile = await getUserProfile()
          if (
            userRequiresPhoneVerification(
              profile.data.celular,
              profile.data.celularConfirmado,
            )
          ) {
            throw redirect({
              to: '/completar-celular',
              search: {
                next: search.next,
                return_app: search.return_app,
              },
            })
          }
        } catch (error) {
          if (isRedirect(error)) {
            throw error
          }
        }

        if (search.return_app === 'suporte') {
          const sup = getSuporteAppBaseUrl()
          if (sup) {
            throw redirect({
              href: getSuporteAppHandoffHref(
                { accessToken: at },
                { next: search.next },
              ),
            })
          }
          return
        }
        if (search.return_app === 'admindesk') {
          const ad = getAdmindeskAppBaseUrl()
          if (ad) {
            throw redirect({
              href: getAdmindeskAppHandoffHref(
                { accessToken: at },
                { next: search.next },
              ),
            })
          }
          return
        }
        if (!mainBase) {
          return
        }
        throw redirect({
          href: getMainAppHandoffHref(
            {
              accessToken: at,
            },
            { next: search.next },
          ),
        })
      }
    }

    const token = normalizeLoginLinkToken(params.token)
    if (!token) {
      throw redirect({
        to: '/',
        search: {
          ...preservedLoginSearch(search),
          error: 'Token inválido',
        },
      })
    }

    try {
      const tokens = await validateOtpByToken(token)
      saveAccessToken(tokens.accessToken)
      await bootstrapRefreshCookie(tokens.accessToken)
      localStorage.setItem('imobdesk_just_logged_in', 'true')
      setTimeout(() => localStorage.removeItem('imobdesk_just_logged_in'), 2000)

      const profile = await getUserProfile().catch(() => null)
      const needsPhone =
        tokens.requiresPhoneVerification === true ||
        userRequiresPhoneVerification(
          profile?.data.celular,
          profile?.data.celularConfirmado,
        )

      if (needsPhone) {
        throw redirect({
          to: '/completar-celular',
          search: {
            next: search.next,
            return_app: search.return_app,
          },
        })
      }

      if (search.return_app === 'suporte') {
        const sup = getSuporteAppBaseUrl()
        if (!sup) {
          throw redirect({
            to: '/',
            search: {
              ...preservedLoginSearch(search),
              error:
                'Central de ajuda não configurada (VITE_SUPORTE_APP_URL). Contate o suporte.',
            },
          })
        }
        throw redirect({
          href: getSuporteAppHandoffHref(tokens, { next: search.next }),
        })
      }
      if (search.return_app === 'admindesk') {
        const ad = getAdmindeskAppBaseUrl()
        if (!ad) {
          throw redirect({
            to: '/',
            search: {
              ...preservedLoginSearch(search),
              error:
                'Contratação não configurada (VITE_ADMINDESK_APP_URL). Contate o suporte.',
            },
          })
        }
        throw redirect({
          href: getAdmindeskAppHandoffHref(tokens, { next: search.next }),
        })
      }
      if (!mainBase) {
        throw redirect({
          to: '/',
          search: {
            ...preservedLoginSearch(search),
            error: 'App principal não configurado (VITE_MAIN_APP_URL).',
          },
        })
      }
      throw redirect({
        href: getMainAppHandoffHref(tokens, { next: search.next }),
      })
    } catch (error) {
      if (isRedirect(error)) {
        throw error
      }
      const apiError = error as ApiErrorResponse
      const errorMessage =
        apiError.message || 'Código inválido ou expirado. Tente novamente.'
      throw redirect({
        to: '/',
        search: {
          ...preservedLoginSearch(search),
          error: errorMessage,
        },
      })
    }
  },
  component: () => null,
})
